import './sentry'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import * as Sentry from '@sentry/react'
import { PostHogProvider } from '@posthog/react'
import { initAppCheck } from './lib/firebaseAi'

// Initialize Firebase App Check eagerly for AI and push notification services
initAppCheck()

// Scrub sensitive OAuth access tokens from URL address bar on initial bootstrap
if (typeof window !== 'undefined' && window.location.hash.includes('access_token=')) {
  setTimeout(() => {
    if (window.location.hash.includes('access_token=')) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, 100);
}

// Handle stale Vite chunk errors gracefully after new deployment
window.addEventListener('vite:preloadError', () => {
  const lastReload = sessionStorage.getItem('tg_chunk_reload_ts');
  const now = Date.now();
  if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
    sessionStorage.setItem('tg_chunk_reload_ts', String(now));
    window.location.reload();
  }
});

// Fallback for dynamic import failures
window.addEventListener('error', (event) => {
  if (event?.message && (
    event.message.includes('Failed to fetch dynamically imported module') ||
    event.message.includes('Expected a JavaScript-or-Wasm module script') ||
    event.message.includes('MIME type of "text/html"')
  )) {
    const lastReload = sessionStorage.getItem('tg_chunk_reload_ts');
    const now = Date.now();
    if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
      sessionStorage.setItem('tg_chunk_reload_ts', String(now));
      window.location.reload();
    }
  }
});

const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://babygirl.tablegreet.com';

const options = {
  api_host: posthogHost,
  capture_pageview: false, // Pageviews are instrumented with React Router in PostHogPageTracker
  capture_pageleave: true,
  autocapture: true,
  capture_dead_clicks: true,
  capture_performance: true,
  enable_recording_console_log: true,
  disable_session_recording: false,
  session_recording: {
    recordCrossOriginIframes: true,
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
    },
  },
  disable_surveys: false,
  cross_subdomain_cookie: true,
  persistence: 'localStorage+cookie' as const,
  loaded(ph: any) {
    try {
      ph.startSessionRecording?.();
    } catch {
      // Graceful fallback if session recording is already in progress
    }
  },
};

// Suppresses only verbose debug messages in production while preserving logs for session replay
if (import.meta.env.PROD) {
  console.debug = () => {};
}

const rootElement = document.getElementById('root')!;

createRoot(rootElement).render(
  <StrictMode>
    <PostHogProvider apiKey={import.meta.env.VITE_POSTHOG_PROJECT_TOKEN} options={options}>
      <Sentry.ErrorBoundary fallback={
        <div style={{ minHeight: '100vh', background: '#0a0a0a', color: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h1 style={{ color: '#ff4f00', marginBottom: 16 }}>Something went wrong</h1>
          <p>Our team has been notified of the issue.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 24, padding: '8px 16px', background: '#222', border: 'none', color: 'white', borderRadius: 4, cursor: 'pointer' }}>
            Reload Page
          </button>
        </div>
      }>
        <App />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </StrictMode>,
);

// Reveal the app: removes the anti-FOUC opacity:0 applied inline in index.html
document.documentElement.classList.add('loaded');
